import dayjs, { Dayjs } from "dayjs";
import duration from "dayjs/plugin/duration";
import { DATE_FORMATS } from "../config/global.config";
dayjs.extend(duration);

export const formatDate = (
  date?: string | Date,
  format: string = DATE_FORMATS.displayDate
) => {
  return dayjs(date || new Date()).format(format);
};

export const formatTime = (date?: string | Date, format: string = "h:mm A") => {
  return dayjs(date || new Date()).format(format);
};

export function formatDuration(minutes: number): string {
  const d = dayjs.duration(minutes, "minutes");
  const hours = d.hours();
  const mins = d.minutes();

  if (hours && mins) return `${hours}h ${mins}m`;
  if (hours) return `${hours}h`;
  if (mins) return `${mins}m`;
  return "0m";
}

export function formatBDMobile(mobile: string): string | null {
  if (!mobile) return null;

  // Remove all non-digit characters
  let normalized = mobile.replace(/\D/g, "");

  // Remove leading 0 or 880
  if (normalized.startsWith("0")) {
    normalized = normalized.slice(1);
  } else if (normalized.startsWith("880")) {
    normalized = normalized.slice(3);
  }

  // Check valid BD operator code
  const validOperators = /^(13|14|15|16|17|18|19)/;
  if (!validOperators.test(normalized) || normalized.length !== 10) {
    return null;
  }

  // Format: +880 17 1234-5678
  const formatted = `+880 ${normalized.slice(0, 4)}-${normalized.slice(4)}`;

  return formatted;
}

export const disablePastDates = (current: Dayjs) => {
  return current && current < dayjs().startOf("day");
};

export const disableBeforeDate = (current: Dayjs, fromDate: Dayjs) => {
  return current && current < fromDate.startOf("day");
};

export const disablePastTimes = (date: Dayjs | null) => {
  if (!date) return {};

  const now = dayjs();
  if (!date.isSame(now, "day")) return {};

  const hours = Array.from({ length: now.hour() }, (_, i) => i);
  const minutes = Array.from({ length: now.minute() }, (_, i) => i);

  return {
    disabledHours: () => hours,
    disabledMinutes: (selectedHour: number) =>
      selectedHour === now.hour() ? minutes : [],
  };
};

export const formatTo12Hour = (time24: string) =>
  dayjs(`2000-01-01 ${time24}`).format("h:mm A");

export const formatSecToTime = (seconds: number) => {
  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;

  return `${mins}:${secs.toString().padStart(2, "0")}`;
};

type TimeResult = {
  convertedTime: number;
  type: "H" | "M";
};

export function convertSeconds(seconds: number): TimeResult {
  const oneHourInSeconds = 3600;

  if (seconds >= oneHourInSeconds) {
    // Convert to hours and keep max 2 decimals
    const hours = seconds / oneHourInSeconds;
    return {
      convertedTime: Number(hours.toFixed(2)),
      type: "H",
    };
  } else {
    // Convert to minutes and keep max 2 decimals
    const minutes = seconds / 60;
    return {
      convertedTime: Number(minutes.toFixed(2)),
      type: "M",
    };
  }
}
